import { useEffect, useMemo, useRef, useState } from 'react';
import { inject } from 'mobx-react';
import { fetch } from '../utils/api-wrapper';
import Utils from '../utils/utils';
import './gl-account-suggest-picker.scss';

const SEGMENT_KEYS = ['gl_unit', 'gl_office', 'gl_section', 'gl_departement', 'gl_timekeeper', 'gl_practice_group'];

// Used for both the segment-config lookup and the line-item JSON field —
// they share the same CamelCase form today.
const SEGMENT_CAMEL = {
    gl_unit: 'GLUnit',
    gl_office: 'GLOffice',
    gl_section: 'GLSection',
    gl_departement: 'GLDepartement',
    gl_timekeeper: 'GLTimekeeper',
    gl_practice_group: 'GLPracticeGroup',
};

// Persisted on the line as `{code}`; elite adapter drops bare strings.
const SYNCED_SEGMENT_KEYS = new Set(['gl_section', 'gl_practice_group']);

const DESCRIPTIVE_KEYS = ['description', 'masked_alias'];
const DESCRIPTIVE_LABEL_KEY = {
    description: 'invoice.gl_suggest_description_header',
    masked_alias: 'invoice.gl_suggest_masked_alias_header',
};

// XPEN-3769: Aderant links an office and a department to an account; the
// suggestion endpoint returns those pairs with their names.
const ADERANT_COLUMNS = [
    { key: 'description', labelKey: DESCRIPTIVE_LABEL_KEY.description, isSegment: false },
    { key: 'gl_natural', labelKey: 'gl_natural', isSegment: true },
    { key: 'office', labelKey: 'office', isSegment: true, labelField: 'office_label' },
    { key: 'department', labelKey: 'department', isSegment: true, labelField: 'department_label' },
];

const DEFAULT_PAGE_SIZE = 100;
// Debounce typing in a column filter so fast typers don't fire a request
// per character; the `ignore` flag below still drops stale responses, but
// debouncing also avoids the network / Mongo cost on the way out.
const FILTER_DEBOUNCE_MS = 250;

const isBlank = (value) => (
    value === null || value === undefined || (typeof value === 'string' && value.trim() === '')
);

const nextSortState = (current, columnId) => {
    if (current?.id !== columnId) return { id: columnId, desc: false };
    if (current.desc === false) return { id: columnId, desc: true };
    return null;
};

const GlAccountSuggestPicker = inject('data')(({ data, initialFilters, onApply, disabled = false }) => {
    const isAderant = Utils.isExportFormat(data, 'aderant');
    const [rows, setRows] = useState([]);
    const [matchMode, setMatchMode] = useState('strict');
    const [totalRowCount, setTotalRowCount] = useState(0);
    // Initial true so the empty-body branch doesn't flash before the first fetch lands.
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    // Pre-fill only the pivot (gl_natural) and gl_unit if the line carries
    // them. Other columns stay empty so the user starts with the broadest
    // useful query and narrows further by typing in any column.
    const seedColumnFilters = useMemo(() => {
        const seed = {};
        if (initialFilters?.gl_natural !== undefined && initialFilters?.gl_natural !== null) {
            seed.gl_natural = String(initialFilters.gl_natural);
        }
        if (initialFilters?.gl_unit !== undefined && initialFilters?.gl_unit !== null) {
            seed.gl_unit = String(initialFilters.gl_unit);
        }
        // The line's office first narrows to its linked departments; with none
        // the endpoint falls back to every pair of the account.
        if (isAderant && !isBlank(initialFilters?.office)) {
            seed.office = String(initialFilters.office);
        }
        return seed;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialFilters?.gl_natural, isAderant ? initialFilters?.office : null]);

    const [columnFilters, setColumnFilters] = useState(seedColumnFilters);
    const [sort, setSort] = useState(null);

    useEffect(() => {
        setColumnFilters(seedColumnFilters);
        setSort(null);
    }, [seedColumnFilters]);

    const filtersKey = `${initialFilters?.gl_natural ?? ''}|${JSON.stringify(columnFilters)}|${JSON.stringify(sort)}`;

    // Effective pivot value drives whether we fire a fetch — picks the user-
    // typed gl_natural first, then falls back to the seed. Empty / missing
    // means "no pivot yet"; skip the fetch and show the empty body so the
    // user can type a value in the column input without an error banner.
    const effectiveGlNatural = (() => {
        const fromColumn = columnFilters?.gl_natural;
        if (typeof fromColumn === 'string' && fromColumn.trim() !== '') return fromColumn;
        if (typeof fromColumn === 'string' && fromColumn.trim() === '') return '';
        const fromSeed = initialFilters?.gl_natural;
        if (fromSeed === undefined || fromSeed === null) return '';
        return String(fromSeed);
    })();

    const debounceRef = useRef(null);
    useEffect(() => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
            debounceRef.current = null;
        }
        if (!effectiveGlNatural) {
            // No pivot → don't hit the endpoint. Clear stale results, drop
            // out of loading so the body shows the empty placeholder.
            setRows([]);
            setMatchMode('strict');
            setTotalRowCount(0);
            setLoading(false);
            setError(false);
            return undefined;
        }
        let ignore = false;
        setLoading(true);
        setError(false);
        const body = {
            ...columnFilters,
            gl_natural: effectiveGlNatural,
            start: 0,
            page_size: DEFAULT_PAGE_SIZE,
            ...(sort ? { sort: [sort] } : {}),
        };
        debounceRef.current = setTimeout(() => {
            fetch(
                '/gl/accounts/suggest/',
                'POST',
                body,
                (response) => {
                    if (ignore) return;
                    const payload = response?.data ?? {};
                    setRows(Array.isArray(payload.gl_accounts) ? payload.gl_accounts : []);
                    setMatchMode(payload?.meta?.match_mode || 'strict');
                    setTotalRowCount(Number(payload?.meta?.totalRowCount) || 0);
                    setLoading(false);
                },
                () => {
                    if (ignore) return;
                    setRows([]);
                    setMatchMode('strict');
                    setTotalRowCount(0);
                    setError(true);
                    setLoading(false);
                },
            );
        }, FILTER_DEBOUNCE_MS);
        return () => {
            ignore = true;
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
                debounceRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtersKey]);

    const segCfg = data?.config?.gl_account_segment_configuration ?? {};
    // Strict opt-in: a segment must be explicitly `active: true` to render
    // as a column. Mirrors the line-summary gate in gl-account-picker-modal
    // so a tenant that hasn't enabled, say, GLOffice never sees the column.
    const isSegmentActive = (key) => segCfg[SEGMENT_CAMEL[key]]?.active === true;
    const activeSegments = SEGMENT_KEYS.filter(isSegmentActive);
    const t = (key) => data.t.read(key);

    const handleApply = (row) => {
        if (isAderant) {
            onApply({
                GLNatural: row.gl_natural,
                Office: row.office,
                DepartmentId: { value: row.department, label: row.department },
            });
            return;
        }
        const segmentValues = {};
        // GLNatural is the pivot, but the picker also lets the user pick a
        // row from a different gl_natural — when that happens the line's
        // GLNatural has to be updated too, otherwise the line keeps its
        // stale pivot and the next validation fails on the old combination.
        if (!isBlank(row.gl_natural)) {
            segmentValues.GLNatural = row.gl_natural;
        }
        activeSegments.forEach((key) => {
            if (isBlank(row[key])) return;
            segmentValues[SEGMENT_CAMEL[key]] = SYNCED_SEGMENT_KEYS.has(key)
                ? { code: String(row[key]) }
                : row[key];
        });
        onApply(segmentValues);
    };

    const setFilter = (key, value) => {
        setColumnFilters((prev) => ({ ...prev, [key]: value }));
    };

    const toggleSort = (columnId) => {
        setSort((prev) => nextSortState(prev, columnId));
    };

    const sortIndicator = (columnId) => {
        if (sort?.id !== columnId) return '';
        return sort.desc ? ' ↓' : ' ↑';
    };

    const allColumns = isAderant ? ADERANT_COLUMNS : [
        { key: 'description', labelKey: DESCRIPTIVE_LABEL_KEY.description, isSegment: false },
        { key: 'gl_natural', labelKey: 'gl_natural', isSegment: true },
        ...activeSegments.map((key) => ({ key, labelKey: `invoice.${key}`, isSegment: true })),
        { key: 'masked_alias', labelKey: DESCRIPTIVE_LABEL_KEY.masked_alias, isSegment: false },
    ];

    // Aderant's endpoint returns an account's few pairs unsorted; sort them here.
    const shownRows = isAderant && sort
        ? [...rows].sort((a, b) => String(a[sort.id] ?? '').localeCompare(String(b[sort.id] ?? '')) * (sort.desc ? -1 : 1))
        : rows;

    const isAnchorOnly = matchMode === 'anchor_only';
    const isTruncated = rows.length > 0 && totalRowCount > rows.length;
    const headerStatus = isAnchorOnly
        ? t('invoice.gl_suggest_anchor_only_banner')
        : isTruncated
            ? t('invoice.gl_suggest_truncated_hint')
                .replace('{shown}', String(rows.length))
                .replace('{total}', String(totalRowCount))
            : '';

    return (
        <div className="gl-account-suggest-picker" data-testid="gl-suggest-picker">
            <div className="gl-account-suggest-picker__header">
                <h5 className="gl-account-suggest-picker__title">
                    {t('invoice.gl_suggest_title')}
                    {loading && (
                        <span
                            className="spinner-border spinner-border-sm ml-2"
                            role="status"
                            aria-live="polite"
                            data-testid="gl-suggest-loading"
                        >
                            <span className="sr-only">{t('loading')}</span>
                        </span>
                    )}
                </h5>
                <div
                    className={`gl-account-suggest-picker__status${isAnchorOnly ? ' gl-account-suggest-picker__status--alert' : ''}`}
                    role={isAnchorOnly ? 'alert' : undefined}
                    data-testid={isAnchorOnly ? 'gl-suggest-anchor-banner' : 'gl-suggest-truncated'}
                    aria-hidden={!headerStatus}
                    style={{ visibility: headerStatus ? 'visible' : 'hidden' }}
                >
                    {headerStatus || ' '}
                </div>
            </div>

            {error && (
                <div
                    className="gl-account-suggest-picker__error"
                    role="alert"
                    data-testid="gl-suggest-error"
                >
                    {t('invoice.gl_suggest_error')}
                </div>
            )}

            {!error && (
                <>

                    <div className="gl-account-suggest-picker__scroll">
                    <table
                        className="gl-account-suggest-picker__table"
                        data-testid="gl-suggest-table"
                    >
                        <thead>
                            <tr>
                                {allColumns.map(({ key, labelKey }) => (
                                    <th
                                        key={key}
                                        className="gl-account-suggest-picker__sortable"
                                        onClick={() => toggleSort(key)}
                                        data-testid={`gl-suggest-header-${key}`}
                                        role="columnheader"
                                    >
                                        {t(labelKey)}{sortIndicator(key)}
                                    </th>
                                ))}
                                <th aria-label="apply" />
                            </tr>
                            <tr className="gl-account-suggest-picker__filter-row">
                                {allColumns.map(({ key }) => (
                                    <th key={key}>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            value={columnFilters[key] ?? ''}
                                            onChange={(e) => setFilter(key, e.target.value)}
                                            placeholder={t('invoice.gl_suggest_filter_placeholder')}
                                            data-testid={`gl-suggest-filter-${key}`}
                                        />
                                    </th>
                                ))}
                                <th aria-label="filter-apply" />
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 && (
                                <tr>
                                    <td
                                        colSpan={allColumns.length + 1}
                                        className="gl-account-suggest-picker__empty"
                                        data-testid={loading ? 'gl-suggest-empty-loading' : 'gl-suggest-empty'}
                                    >
                                        {loading ? t('loading') : t('invoice.gl_suggest_no_results')}
                                    </td>
                                </tr>
                            )}
                            {shownRows.map((row, index) => (
                                <tr key={row._id || index}>
                                    {allColumns.map(({ key, isSegment, labelField }) => (
                                        <td
                                            key={key}
                                            data-testid={`gl-suggest-cell-${key}-${index}`}
                                        >
                                            {isBlank(row[key])
                                                ? '—'
                                                : (isSegment ? <strong>{row[key]}</strong> : row[key])}
                                            {labelField && !isBlank(row[labelField]) && row[labelField] !== row[key]
                                                && <span className="ml-1">{row[labelField]}</span>}
                                        </td>
                                    ))}
                                    <td>
                                        <button
                                            type="button"
                                            className="btn btn-primary btn-sm"
                                            disabled={disabled}
                                            onClick={() => handleApply(row)}
                                            data-testid={`gl-suggest-apply-${index}`}
                                        >
                                            {t('invoice.gl_suggest_apply')}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    </div>
                </>
            )}
        </div>
    );
});

export default GlAccountSuggestPicker;
